import { create } from 'zustand';
import type {
  AdminApiError,
  AdminCompanyDto,
  AdminCompanyBulkAction,
  AdminCompanyBulkDeletePreviews,
  AdminCompanyBulkOutcome,
  AdminCompanyCreateRequest,
  AdminCompanyDeleteOptions,
  AdminCompanyDetailDto,
  AdminCompanyPatchRequest,
  AdminHealthDto,
  AdminLicenseBulkAction,
  AdminLicenseCreateRequest,
  AdminLicenseDetailDto,
  AdminLicenseDto,
  AdminLicenseBulkOutcome,
  AdminLicensePatchRequest,
  AdminLicenseSaveResult,
  AdminMutationOutcome,
  AdminTicketDto,
  AdminTicketListItemDto,
  AdminTicketListQuery,
  AdminTicketListResponse,
  AdminTicketMessageRequest,
  AdminTicketPatchRequest,
  AdminUserBulkAction,
  AdminUserBulkOutcome,
  AdminUserCreateRequest,
  AdminUserDto,
  AdminUserPatchRequest,
  DashboardStatsDto,
} from '../types';
import { superAdminApi } from '../api/superAdminApi';
import { createLicenseStoreActions } from './licenseStoreActions';
import { createCompanyStoreActions } from './companyStoreActions';
import { createUserStoreActions } from './userStoreActions';
import { createRequestStoreActions } from './requestStoreActions';
import type {
  AdminDemoLeadConversionInput,
  AdminDemoLeadConversionJobStatus,
  AdminDemoLeadConversionResult,
  AdminDemoLeadDetail,
  AdminDemoLeadListQuery,
  AdminDemoLeadPage,
  AdminDemoLeadStatusRequest,
  AdminDocumentPackageBundle,
  AdminDocumentPackageDetail,
  AdminDocumentPackageListItem,
  AdminDocumentPackagePage,
  AdminDocumentPackageSearchQuery,
  AdminDocumentSignerPlanEntry,
  AdminLegalProfile,
  AdminLegalProfileUpdate,
  AdminLegalSignatureEvidence,
  ETagged,
  LegalPackageCommand,
  LegalSignerCommand,
  SignedArtifactUploadInput,
} from '../legalTypes';
import {
  createDemoLeadStoreActions,
  EmptyDemoLeadPage,
} from './demoLeadStoreActions';
import { createLegalDocumentStoreActions } from './legalDocumentStoreActions';

export interface AdminDocumentConflict {
  error: AdminApiError;
  local: AdminDocumentPackageDetail;
  current: AdminDocumentPackageDetail;
}

export interface AdminLegalProfileConflict {
  error: AdminApiError;
  local: ETagged<AdminLegalProfile>;
  current: AdminLegalProfile;
}

export interface AdminCompanyLegalSummary {
  companyId: number;
  profile: ETagged<AdminLegalProfile> | null;
  packages: AdminDocumentPackageListItem[];
  retainedEvidence: boolean;
}

export interface SuperAdminState {
  demoLeads: AdminDemoLeadPage;
  demoLeadDetail: AdminDemoLeadDetail | null;
  demoLeadConversion: AdminDemoLeadConversionResult | null;
  demoLeadConversionConflict: {
    error: AdminApiError;
    existing: AdminDemoLeadConversionResult;
  } | null;
  demoLeadConversionJob: AdminDemoLeadConversionJobStatus | null;
  demoLeadLoading: boolean;
  demoLeadDetailLoading: boolean;
  demoLeadMutating: boolean;
  demoLeadConverting: boolean;
  demoLeadError: AdminApiError | null;
  documentPackages: AdminDocumentPackageListItem[];
  documentPackagePage: AdminDocumentPackagePage | null;
  documentPackage: ETagged<AdminDocumentPackageDetail> | null;
  documentBundle: AdminDocumentPackageBundle | null;
  legalProfile: ETagged<AdminLegalProfile> | null;
  legalProfileConflict: AdminLegalProfileConflict | null;
  documentListLoading: boolean;
  documentDetailLoading: boolean;
  documentMutationLoading: boolean;
  documentError: AdminApiError | null;
  documentPartialError: string | null;
  documentConflict: AdminDocumentConflict | null;
  companyLegalSummary: AdminCompanyLegalSummary | null;
  companyLegalSummaryLoading: boolean;
  companyLegalSummaryError: AdminApiError | null;
  dashboard: DashboardStatsDto | null;
  health: AdminHealthDto;
  expiringLicenses: AdminLicenseDto[];
  latestTickets: AdminTicketDto[];
  requestTickets: AdminTicketListItemDto[];
  requestDetail: AdminTicketDto | null;
  requestLoading: boolean;
  requestLoadingMore: boolean;
  requestDetailLoading: boolean;
  requestSubmitting: boolean;
  requestError: AdminApiError | null;
  requestNextCursor: string | null;
  requestStatusCounts: AdminTicketListResponse['statusCounts'];
  requestCategoryCounts: AdminTicketListResponse['categoryCounts'];
  licenses: AdminLicenseDto[];
  licenseDetail: AdminLicenseDetailDto | null;
  licenseLoading: boolean;
  licenseError: AdminApiError | null;
  users: AdminUserDto[];
  userDetail: AdminUserDto | null;
  userLoading: boolean;
  userError: AdminApiError | null;
  companies: AdminCompanyDto[];
  companyDetail: AdminCompanyDetailDto | null;
  companyLoading: boolean;
  companyError: string | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
}

export interface SuperAdminActions {
  fetchDemoLeads: (query: AdminDemoLeadListQuery) => Promise<AdminDemoLeadPage>;
  loadDemoLead: (leadId: number) => Promise<AdminDemoLeadDetail>;
  clearDemoLead: () => void;
  updateDemoLeadStatus: (
    leadId: number,
    request: AdminDemoLeadStatusRequest
  ) => Promise<AdminDemoLeadDetail>;
  convertDemoLead: (
    leadId: number,
    input: AdminDemoLeadConversionInput
  ) => Promise<AdminDemoLeadConversionResult>;
  loadDemoLeadConversionJob: (
    leadId: number
  ) => Promise<AdminDemoLeadConversionJobStatus | null>;
  clearDemoLeadConversion: () => void;
  fetchDocumentPackages: (
    query: AdminDocumentPackageSearchQuery
  ) => Promise<AdminDocumentPackagePage>;
  resolveDocumentPackageCompany: (packageId: string) => Promise<number | null>;
  loadDocumentWorkspace: (companyId: number, packageId: string) => Promise<void>;
  clearDocumentWorkspace: () => void;
  loadCompanyLegalSummary: (companyId: number) => Promise<void>;
  clearCompanyLegalSummary: () => void;
  saveLegalProfile: (
    companyId: number,
    input: AdminLegalProfileUpdate
  ) => Promise<ETagged<AdminLegalProfile>>;
  refreshLegalProfileConflict: () => Promise<void>;
  executeDocumentCommand: (
    command: LegalPackageCommand
  ) => Promise<AdminDocumentPackageDetail>;
  refreshDocumentConflict: () => Promise<void>;
  configureDocumentSigners: (
    signers: AdminDocumentSignerPlanEntry[]
  ) => Promise<AdminDocumentPackageDetail>;
  executeDocumentSignerCommand: (
    signerId: string,
    command: LegalSignerCommand
  ) => Promise<AdminDocumentPackageDetail>;
  completeDocumentSigner: (signerId: string) => Promise<AdminDocumentPackageDetail>;
  uploadDocumentEvidence: (
    signerId: string,
    input: SignedArtifactUploadInput
  ) => Promise<AdminLegalSignatureEvidence>;
  downloadDocumentArtifact: (documentId: string) => Promise<void>;
  downloadDocumentPackage: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  fetchDashboard: () => Promise<void>;
  fetchLicenses: () => Promise<void>;
  loadLicenseDetail: (id: number) => Promise<AdminLicenseDetailDto>;
  clearLicenseDetail: () => void;
  createLicense: (request: AdminLicenseCreateRequest) => Promise<AdminLicenseDetailDto>;
  saveLicense: (
    id: number,
    patch: AdminLicensePatchRequest | null,
    pendingRevokeIds: readonly number[]
  ) => Promise<AdminLicenseSaveResult>;
  deleteLicense: (id: number) => Promise<AdminMutationOutcome>;
  bulkLicenses: (
    action: AdminLicenseBulkAction,
    ids: number[],
    months?: number
  ) => Promise<AdminLicenseBulkOutcome>;
  fetchUsers: () => Promise<void>;
  loadUserDetail: (id: number) => Promise<AdminUserDto>;
  clearUserDetail: () => void;
  createUser: (request: AdminUserCreateRequest) => Promise<AdminUserDto>;
  saveUser: (id: number, patch: AdminUserPatchRequest) => Promise<AdminUserDto>;
  deleteUser: (id: number) => Promise<AdminMutationOutcome>;
  resetUserPassword: (id: number) => Promise<void>;
  bulkUsers: (
    action: AdminUserBulkAction,
    ids: number[]
  ) => Promise<AdminUserBulkOutcome>;
  fetchCompanies: () => Promise<void>;
  loadCompanyDetail: (id: number) => Promise<AdminCompanyDetailDto>;
  clearCompanyDetail: () => void;
  createCompany: (request: AdminCompanyCreateRequest) => Promise<AdminCompanyDetailDto>;
  saveCompany: (id: number, patch: AdminCompanyPatchRequest) => Promise<AdminCompanyDetailDto>;
  deleteCompany: (
    id: number,
    options: AdminCompanyDeleteOptions
  ) => Promise<AdminMutationOutcome>;
  bulkCompanies: (
    action: AdminCompanyBulkAction,
    ids: number[],
    expectedCounts?: AdminCompanyBulkDeletePreviews
  ) => Promise<AdminCompanyBulkOutcome>;
  fetchTickets: (query: AdminTicketListQuery, mode?: 'replace' | 'append') => Promise<AdminTicketListResponse>;
  loadTicketDetail: (id: number) => Promise<AdminTicketDto>;
  clearTicketDetail: () => void;
  patchTicket: (
    id: number,
    patch: AdminTicketPatchRequest,
    optimistic?: Pick<AdminTicketDto, 'status' | 'priority' | 'assigneeId' | 'assigneeName'>
  ) => Promise<AdminTicketDto>;
  sendTicketMessage: (id: number, request: AdminTicketMessageRequest) => Promise<AdminTicketDto>;
  resolveTicket: (id: number, expectedUpdatedAt: string) => Promise<AdminTicketDto>;
  reopenTicket: (id: number, expectedUpdatedAt: string) => Promise<AdminTicketDto>;
  fetchAll: () => Promise<void>;
  reset: () => void;
}

interface DashboardResults {
  dashboard: PromiseSettledResult<DashboardStatsDto>;
  health: PromiseSettledResult<AdminHealthDto>;
  expiringLicenses: PromiseSettledResult<AdminLicenseDto[]>;
  latestTickets: PromiseSettledResult<AdminTicketDto[]>;
}

const OfflineHealth: AdminHealthDto = { licenseServer: 'offline' };

const initialState: SuperAdminState = {
  demoLeads: EmptyDemoLeadPage,
  demoLeadDetail: null,
  demoLeadConversion: null,
  demoLeadConversionConflict: null,
  demoLeadConversionJob: null,
  demoLeadLoading: false,
  demoLeadDetailLoading: false,
  demoLeadMutating: false,
  demoLeadConverting: false,
  demoLeadError: null,
  documentPackages: [],
  documentPackagePage: null,
  documentPackage: null,
  documentBundle: null,
  legalProfile: null,
  legalProfileConflict: null,
  documentListLoading: false,
  documentDetailLoading: false,
  documentMutationLoading: false,
  documentError: null,
  documentPartialError: null,
  documentConflict: null,
  companyLegalSummary: null,
  companyLegalSummaryLoading: false,
  companyLegalSummaryError: null,
  dashboard: null,
  health: OfflineHealth,
  expiringLicenses: [],
  latestTickets: [],
  requestTickets: [],
  requestDetail: null,
  requestLoading: false,
  requestLoadingMore: false,
  requestDetailLoading: false,
  requestSubmitting: false,
  requestError: null,
  requestNextCursor: null,
  requestStatusCounts: { new: 0, open: 0, resolved: 0 },
  requestCategoryCounts: { bug: 0, question: 0, billing: 0, errorReport: 0 },
  licenses: [],
  licenseDetail: null,
  licenseLoading: true,
  licenseError: null,
  users: [],
  userDetail: null,
  userLoading: false,
  userError: null,
  companies: [],
  companyDetail: null,
  companyLoading: false,
  companyError: null,
  loading: false,
  error: null,
  stale: false,
};

const reasonMessage = (reason: unknown) =>
  reason instanceof Error ? reason.message : 'Request failed';

const resultError = (label: string, result: PromiseSettledResult<unknown>) =>
  result.status === 'rejected' ? `${label}: ${reasonMessage(result.reason)}` : null;

const loadDashboardResults = async (): Promise<DashboardResults> => {
  const [dashboard, health, expiringLicenses, latestTickets] =
    await Promise.allSettled([
      superAdminApi.getDashboard(),
      superAdminApi.getHealth(),
      superAdminApi.getExpiringLicenses(),
      superAdminApi.getLatestTickets(),
    ]);

  return { dashboard, health, expiringLicenses, latestTickets };
};

const dashboardPatch = (
  state: SuperAdminState,
  results: DashboardResults
): Partial<SuperAdminState> => {
  const failures = [
    resultError('Dashboard', results.dashboard),
    resultError('Health', results.health),
    resultError('Expiring licenses', results.expiringLicenses),
    resultError('Latest requests', results.latestTickets),
  ].filter((message): message is string => Boolean(message));

  return {
    dashboard:
      results.dashboard.status === 'fulfilled'
        ? results.dashboard.value
        : state.dashboard,
    health:
      results.health.status === 'fulfilled' ? results.health.value : OfflineHealth,
    expiringLicenses:
      results.expiringLicenses.status === 'fulfilled'
        ? results.expiringLicenses.value
        : state.expiringLicenses,
    latestTickets:
      results.latestTickets.status === 'fulfilled'
        ? results.latestTickets.value
        : state.latestTickets,
    loading: false,
    error: failures.length > 0 ? failures.join(' \u00b7 ') : null,
    stale: failures.length > 0,
  };
};

export const useSuperAdminStore = create<SuperAdminState & SuperAdminActions>(
  (set, get) => {
    let refreshInFlight: Promise<void> | null = null;
    const licenseActions = createLicenseStoreActions(set, get);
    const userActions = createUserStoreActions(set);
    const companyActions = createCompanyStoreActions(set, get);
    const requestActions = createRequestStoreActions(set, get);
    const demoLeadActions = createDemoLeadStoreActions(set);
    const legalDocumentActions = createLegalDocumentStoreActions(set, get);

    const refreshDashboard = () => {
      if (refreshInFlight) {
        return refreshInFlight;
      }

      set({ loading: true, error: null });
      refreshInFlight = loadDashboardResults()
        .then((results) => set((state) => dashboardPatch(state, results)))
        .finally(() => {
          refreshInFlight = null;
        });
      return refreshInFlight;
    };

    const fetchAll = async () => {
      await Promise.allSettled([
        refreshDashboard(),
        licenseActions.fetchLicenses(),
        userActions.fetchUsers(),
        companyActions.fetchCompanies(),
      ]);
    };

    return {
      ...initialState,
      refreshDashboard,
      fetchDashboard: refreshDashboard,
      ...licenseActions,
      ...userActions,
      ...companyActions,
      ...requestActions,
      ...demoLeadActions,
      ...legalDocumentActions,
      fetchAll,
      reset: () => set(initialState),
    };
  }
);
